// Shared constants for roles, categories, statuses

export const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  OWNER: "Owner",
  CENTRAL_PURCHASING: "Central Purchasing",
  WAREHOUSE: "Warehouse",
  BRANCH_STAFF: "Branch Staff",
  BRANCH_MANAGER: "Branch Manager",
  KITCHEN: "Kitchen / Head Chef",
  HQ_OPERATIONS: "HQ Operations",
  CHECKER_CENTRAL: "Checker Central",
  FINANCE_STAFF: "Finance Staff",
  FINANCE_MANAGER: "Finance Manager",
  HR_MANAGER: "HR Manager",
};

export const CATEGORY_COLORS = {
  meat: { bg: "#FFE4E6", text: "#E11D48", border: "#FDA4AF" },
  seafood: { bg: "#DBEAFE", text: "#2563EB", border: "#93C5FD" },
  vegetables: { bg: "#D1FAE5", text: "#059669", border: "#6EE7B7" },
  spices: { bg: "#FEF3C7", text: "#D97706", border: "#FCD34D" },
  packaging: { bg: "#F1F5F9", text: "#475569", border: "#CBD5E1" },
  beverages: { bg: "#CCFBF1", text: "#0D9488", border: "#5EEAD4" },
};

export const CATEGORY_ICONS = {
  meat: "drumstick",
  seafood: "fish",
  vegetables: "carrot",
  spices: "flame",
  packaging: "package",
  beverages: "cup-soda",
};

export const ORDER_STATUS = {
  DRAFT: { label: "Draft", tone: "mute" },
  SUBMITTED: { label: "Submitted", tone: "warn" },
  APPROVED: { label: "Approved", tone: "info" },
  PURCHASED: { label: "Purchased", tone: "info" },
  DELIVERY_ADJUSTMENT: { label: "Adjusting", tone: "warn" },
  DELIVERED: { label: "Delivered", tone: "success" },
  RECEIVED: { label: "Received", tone: "success" },
};

export const STOCK_STATUS = {
  SAFE: { label: "Safe", tone: "success" },
  LOW: { label: "Low", tone: "warn" },
  CRITICAL: { label: "Critical", tone: "danger" },
};

export const WASTE_CATEGORIES = [
  { value: "EXPIRED", label: "Expired" },
  { value: "SPOILED", label: "Spoiled" },
  { value: "CUSTOMER_COMPLAINT", label: "Customer Complaint" },
  { value: "KITCHEN_ERROR", label: "Kitchen Error" },
  { value: "MISSING_LOST", label: "Missing / Lost" },
  { value: "BROKEN_PACKAGING", label: "Broken Packaging" },
];

export const CHECKLIST_STATUS = ["AVAILABLE", "PARTIAL", "UNAVAILABLE"];

// Local brand assets (self-hosted in /public/brand — no external CDN dependency)
const BRAND = `${process.env.PUBLIC_URL || ""}/brand`;

export const MASCOTS = {
  order: `${BRAND}/mascot-order.png`,
  inventory: `${BRAND}/mascot-inventory.png`,
  waste: `${BRAND}/mascot-waste.png`,
  loginBg: "https://images.unsplash.com/photo-1577219492769-b63a779fac28?crop=entropy&cs=srgb&fm=jpg&q=85",
};

export const fmtRp = (v) =>
  "Rp " + Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export const fmtNum = (v) => {
  const n = Number(v || 0);
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

// Always show a unit with a quantity, e.g. "3 KG", "500 GRAM"
export const fmtUnit = (u) => (u ? String(u).toUpperCase() : "");
export const fmtQtyUnit = (qty, unit) => `${fmtNum(qty)} ${fmtUnit(unit)}`.trim();

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Date-only formatter → "27 Jun 2026" (handles YYYY-MM-DD and ISO).
export const fmtDateWIB = (val) => {
  if (!val) return "—";
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]} ${MON[parseInt(m[2], 10) - 1]} ${m[1]}`;
  const d = new Date(val);
  return isNaN(d) ? String(val) : `${String(d.getDate()).padStart(2, "0")} ${MON[d.getMonth()]} ${d.getFullYear()}`;
};

// Timestamp formatter in Jakarta time → "26 Jun 2026 • 22:48 WIB".
export const fmtWIB = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta",
  }).formatToParts(d);
  const g = (t) => (parts.find((p) => p.type === t) || {}).value || "";
  return `${g("day")} ${g("month")} ${g("year")} • ${g("hour")}:${g("minute")} WIB`;
};

// Consistent order listing label: "Branch Name - DD/MM/YYYY"
export const orderLabel = (o) => {
  if (!o) return "";
  if (!o.branch_name) return o.order_no || "";
  if (!o.order_date) return o.branch_name;
  const [y, m, d] = String(o.order_date).split("-");
  return `${o.branch_name} - ${d}/${m}/${y}`;
};

// Which department currently "owns" the order (item ownership clarity).
export const orderOwner = (o) => {
  if (!o) return null;
  const s = o.status, ff = o.fulfillment_status;
  if (s === "DRAFT") return { key: "branch", label: "Branch", tone: "mute" };
  if (s === "SUBMITTED" || s === "APPROVED") return { key: "purchasing", label: "Central Purchasing", tone: "info" };
  if (s === "PURCHASED") {
    if (ff === "READY" || ff === "LOADED" || ff === "IN_TRANSIT") return { key: "driver", label: "Delivery", tone: "info" };
    return { key: "warehouse", label: "Central Warehouse", tone: "warn" };
  }
  if (s === "DELIVERY_ADJUSTMENT") return { key: "warehouse", label: "Central Warehouse", tone: "warn" };
  if (s === "DELIVERED") return { key: "branch", label: "Branch Receiving", tone: "warn" };
  if (s === "RECEIVED") return { key: "done", label: "Completed", tone: "success" };
  return null;
};

// Friendly restaurant illustrations + category imagery (self-hosted)
export const CATEGORY_ILLUSTRATIONS = {
  meat: `${BRAND}/cat-meat.png`,
  seafood: `${BRAND}/cat-seafood.png`,
  vegetables: `${BRAND}/cat-vegetables.png`,
  packaging: `${BRAND}/cat-packaging.png`,
  spices: "https://images.unsplash.com/photo-1565279133304-39470075be5a?crop=entropy&cs=srgb&fm=jpg&q=70&w=400",
  beverages: "https://images.unsplash.com/photo-1625631978414-cce4a3ca61aa?crop=entropy&cs=srgb&fm=jpg&q=70&w=400",
};

export const ASSETS = {
  emptyOrders: `${BRAND}/empty-orders.png`,
  stockGood: `${BRAND}/stock-good.png`,
  productPlaceholder: "https://images.unsplash.com/photo-1659542134727-02910d17c06b?crop=entropy&cs=srgb&fm=jpg&q=70&w=400",
};

export const catIllustration = (colorKey) => CATEGORY_ILLUSTRATIONS[colorKey] || CATEGORY_ILLUSTRATIONS.packaging;
